// components/analytics/components/charts/PieChart.tsx
import { Pie } from '@antv/g2plot';
import { useEffect, useRef } from 'react';
import { Skeleton } from 'antd';
import NoDataPlaceholder from '../NoDataPlaceholder';

interface PieChartProps {
  data: Array<{
    [key: string]: any;
  }>;
  nameField: string;
  valueField: string;
  height?: number;
  innerRadius?: number;
  colorField?: string;
  loading?: boolean;
}

export const PieChart: React.FC<PieChartProps> = ({
  data,
  nameField,
  valueField,
  height = 300,
  innerRadius = 0,
  colorField,
  loading = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Pie | null>(null);

  useEffect(() => {
    // Guard clause to ensure container exists
    if (!containerRef.current) {
      return;
    }

    // Guard clause to check if data exists
    if (!data || data.length === 0) {
      return;
    }

    // Cleanup previous instance
    if (chartRef.current) {
      try {
        chartRef.current.destroy();
      } catch (e) {
        console.error('Error destroying previous chart:', e);
      }
    }

    try {
      // Create new instance
              const chart = new Pie(containerRef.current, {
          data,
          angleField: valueField,
          colorField: colorField || nameField,
          radius: 0.8,
          innerRadius,
        label: {
          type: 'spider',
          content: '{name}: {percentage}',
        },
        interactions: [
          { type: 'element-active' },
          { type: 'legend-highlight' }
        ],
        statistic: {
          title: false,
          content: {
            style: {
              whiteSpace: 'pre-wrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
                         content: 'Total\n' + data.reduce((sum, item) => sum + Number(item[valueField]), 0),
          },
        },
        animation: {
          appear: {
            animation: 'fade-in',
            duration: 1000
          }
        }
      });

      chartRef.current = chart;
      chart.render();
    } catch (e) {
      console.error('Error creating chart:', e);
    }

    // Cleanup function
    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
          chartRef.current = null;
        } catch (e) {
          console.error('Error destroying chart during cleanup:', e);
        }
      }
    };
  }, [data, nameField, valueField, innerRadius, colorField, loading]);

  // Show skeleton while loading
  if (loading) {
    return (
      <div style={{ height, padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <NoDataPlaceholder height={height} />;
  }

  return <div ref={containerRef} style={{ height }} />;
};